import { ReactNode } from "react";
import { GatedContainer } from "@/components/shared/GatedContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { DemoPreviewBanner } from "@/components/billing/DemoPreviewBanner";
import { useEntitlements } from "@/hooks/useEntitlements";
import { usePortfolioPaidAccess } from "@/hooks/usePortfolioPaidAccess";

type PaidRouteProps = {
  children: ReactNode;
  title: string;
  description: string;
};

/**
 * Guards a paid-only page. The page itself still renders, blurred, behind the same
 * upgrade CTA used on the public stock research pages, rather than being replaced. The
 * matching API endpoints are gated server-side too, so the blurred content behind the
 * overlay never carries real data for a free-tier user. The page's own title stays sharp
 * above the blur, matching the title each page already shows once unlocked.
 *
 * `allowDemoPreview` is for pages that run on the selected portfolio: the demo unlocks them,
 * so a free user can try the page on it, with a banner saying so.
 */
export default function PaidRoute({
  allowDemoPreview = false,
  ...props
}: PaidRouteProps & { allowDemoPreview?: boolean }) {
  return allowDemoPreview ? (
    <PortfolioPaidRoute {...props} />
  ) : (
    <UserPaidRoute {...props} />
  );
}

function UserPaidRoute({ children, title, description }: PaidRouteProps) {
  const { isPaid, isLoading } = useEntitlements();

  if (isLoading) {
    return null;
  }

  if (isPaid) {
    return <>{children}</>;
  }

  return (
    <Gate title={title} description={description}>
      {children}
    </Gate>
  );
}

function PortfolioPaidRoute({ children, title, description }: PaidRouteProps) {
  const { canUse, isDemoPreview, isLoading } = usePortfolioPaidAccess();

  if (isLoading) {
    return null;
  }

  if (isDemoPreview) {
    return (
      <>
        <div className="px-10 pt-10">
          <DemoPreviewBanner />
        </div>
        {children}
      </>
    );
  }

  if (canUse) {
    return <>{children}</>;
  }

  return (
    <Gate title={title} description={description} showDemoOption>
      {children}
    </Gate>
  );
}

function Gate({
  children,
  title,
  description,
  showDemoOption = false,
}: PaidRouteProps & { showDemoOption?: boolean }) {
  return (
    <div className="p-10 space-y-8">
      <PageHeader title={title} />
      <GatedContainer
        isLoggedIn={false}
        variant="upgrade"
        description={description}
        frameless
        showDemoOption={showDemoOption}
      >
        {children}
      </GatedContainer>
    </div>
  );
}
